import { IssueCaseCategory, IssueCaseStatusCode, IssueTaskCategory, IssueTaskStatus } from '@gooduncles/gu-app-schema';
import { Card, Col, Row, Statistic, Typography } from 'antd';
import { FC } from 'react';
import {
  ISSUE_CASE_ACTIVE_STATUSES,
  ISSUE_CASE_ALL_CATEGORIES,
  ISSUE_TASK_ACTIVE_STATUSES,
  ISSUE_TASK_ALL_CATEGORIES,
  issueTaskCategoryKr,
} from 'src/constants/issue.constants';

import { categoryColorMap } from 'src/utils/issue/issue-ag-grid-util';
import { getCategoryLabel } from 'src/utils/issue/issue-data-util';
import {
  issueCaseStatusBadgeMap,
  issueStatusColorMap,
  issueTaskCategoryColorMap,
  issueTaskStatusBadgeMap,
} from 'src/utils/issue/issue-util';

import IssueRankingList from 'src/components/Issue/common/IssueRankingList';

import { IssueCaseSummary, IssueTaskSummary } from 'src/hooks/subscription/useIssueHistoryData';

const { Text } = Typography;

interface IssueCaseSummaryProps {
  summary: IssueCaseSummary;
  isLoading: boolean;
}

export const IssueCaseSummaryCard: FC<IssueCaseSummaryProps> = ({ summary, isLoading }) => {
  // 모든 상태/분류에 대해 count가 없으면 0으로 표시
  const statusEntries = ISSUE_CASE_ACTIVE_STATUSES.map(
    (status) => [status, summary.byStatus[status] || 0] as [IssueCaseStatusCode, number]
  );
  const categoryEntries = ISSUE_CASE_ALL_CATEGORIES.map(
    (category) => [category, summary.byCategory[category] || 0] as [IssueCaseCategory, number]
  );

  return (
    <Card size='small' title='이슈 케이스 요약' loading={isLoading}>
      <Row gutter={[24, 16]}>
        {/* 전체 건수 */}
        <Col span={3}>
          <Statistic title='전체' value={summary.total} suffix='건' valueStyle={{ fontSize: 24, fontWeight: 600 }} />
        </Col>

        {/* 상태별 */}
        <Col span={6}>
          <Text strong style={{ fontSize: 13, color: '#666', marginBottom: 8, display: 'block' }}>
            상태별
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 8px' }}>
            {statusEntries.map(([status, count]) => {
              const badge = issueCaseStatusBadgeMap[status];
              const color = badge ? issueStatusColorMap[badge.status] : '#8c8c8c';
              return (
                <div key={status} style={{ textAlign: 'center', minWidth: 40 }}>
                  <div style={{ color, fontSize: 18, fontWeight: 600 }}>{count}</div>
                  <div style={{ fontSize: 11, color: '#666' }}>{badge?.text || status}</div>
                </div>
              );
            })}
          </div>
        </Col>

        {/* 분류별 */}
        <Col span={5}>
          <Text strong style={{ fontSize: 13, color: '#666', marginBottom: 8, display: 'block' }}>
            분류별
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 8px' }}>
            {categoryEntries.map(([category, count]) => {
              const color = categoryColorMap[category] || '#8c8c8c';
              const label = getCategoryLabel(category);
              return (
                <div key={category} style={{ textAlign: 'center', minWidth: 40 }}>
                  <div style={{ color, fontSize: 18, fontWeight: 600 }}>{count}</div>
                  <div style={{ fontSize: 11, color }}>{label}</div>
                </div>
              );
            })}
          </div>
        </Col>

        {/* 상품 Top 5 */}
        <Col span={5}>
          <IssueRankingList title='이슈 상품 Top 5' items={summary.topProducts} emptyText='상품 데이터 없음' />
        </Col>

        {/* 매장 Top 5 */}
        <Col span={5}>
          <IssueRankingList title='이슈 매장 Top 5' items={summary.topStores} emptyText='매장 데이터 없음' />
        </Col>
      </Row>
    </Card>
  );
};

interface IssueTaskSummaryProps {
  summary: IssueTaskSummary;
  isLoading: boolean;
}

export const IssueTaskSummaryCard: FC<IssueTaskSummaryProps> = ({ summary, isLoading }) => {
  // 모든 상태/분류에 대해 count가 없으면 0으로 표시
  const statusEntries = ISSUE_TASK_ACTIVE_STATUSES.map(
    (status) => [status, summary.byStatus[status] || 0] as [IssueTaskStatus, number]
  );
  const categoryEntries = ISSUE_TASK_ALL_CATEGORIES.map(
    (category) => [category, summary.byCategory[category] || 0] as [IssueTaskCategory, number]
  );

  return (
    <Card size='small' title='이슈 태스크 요약' loading={isLoading}>
      <Row gutter={[24, 16]}>
        {/* 전체 건수 */}
        <Col span={3}>
          <Statistic title='전체' value={summary.total} suffix='건' valueStyle={{ fontSize: 24, fontWeight: 600 }} />
        </Col>

        {/* 상태별 */}
        <Col span={6}>
          <Text strong style={{ fontSize: 13, color: '#666', marginBottom: 8, display: 'block' }}>
            상태별
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 8px' }}>
            {statusEntries.map(([status, count]) => {
              const badge = issueTaskStatusBadgeMap[status];
              const color = badge ? issueStatusColorMap[badge.status] : '#8c8c8c';
              return (
                <div key={status} style={{ textAlign: 'center', minWidth: 40 }}>
                  <div style={{ color, fontSize: 18, fontWeight: 600 }}>{count}</div>
                  <div style={{ fontSize: 11, color: '#666' }}>{badge?.text || status}</div>
                </div>
              );
            })}
          </div>
        </Col>

        {/* 분류별 */}
        <Col span={5}>
          <Text strong style={{ fontSize: 13, color: '#666', marginBottom: 8, display: 'block' }}>
            분류별
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 8px' }}>
            {categoryEntries.map(([category, count]) => {
              const color = issueTaskCategoryColorMap[category] || '#8c8c8c';
              const label = issueTaskCategoryKr[category] || category;
              return (
                <div key={category} style={{ textAlign: 'center', minWidth: 40 }}>
                  <div style={{ color, fontSize: 18, fontWeight: 600 }}>{count}</div>
                  <div style={{ fontSize: 11, color }}>{label}</div>
                </div>
              );
            })}
          </div>
        </Col>

        {/* 상품 Top 5 */}
        <Col span={5}>
          <IssueRankingList title='처리 상품 Top 5' items={summary.topProducts} emptyText='상품 데이터 없음' />
        </Col>

        {/* 매장 Top 5 */}
        <Col span={5}>
          <IssueRankingList title='처리 매장 Top 5' items={summary.topStores} emptyText='매장 데이터 없음' />
        </Col>
      </Row>
    </Card>
  );
};
